import type { DailyPromoteResult, LibraryToolsResponse, LibraryToolsRow } from '@zinc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { ScoreChip } from '../../components/ScoreChip';
import { Panel, tableCell, tinyButton, when } from './parts';

/** Admin → Library tools: hide or delete a puzzle, make one today's daily; merge two duplicates
 * by hand. */
export function AdminLibraryToolsPage() {
  const client = useQueryClient();
  const [q, setQ] = useState('');
  const [onlyHidden, setOnlyHidden] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [keep, setKeep] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (onlyHidden) params.set('hidden', 'only');
  const data = useQuery({
    queryKey: ['admin', 'library', q.trim(), onlyHidden],
    queryFn: () => api<LibraryToolsResponse>(`/api/admin/library?${params.toString()}`),
  });
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['admin', 'library'] });
    void client.invalidateQueries({ queryKey: ['library'] });
  };
  const hide = useMutation({
    mutationFn: (p: LibraryToolsRow) =>
      api(`/api/admin/library/${p.id}/hidden`, { method: 'PUT', json: { hidden: !p.hidden } }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (p: LibraryToolsRow) => api(`/api/admin/library/${p.id}`, { method: 'DELETE' }),
    onSuccess: (_r, p) => {
      setMessage(`Deleted “${p.title}”.`);
      setPicked((x) => x.filter((id) => id !== p.id));
      refresh();
    },
  });
  const daily = useMutation({
    mutationFn: (p: LibraryToolsRow) =>
      api<DailyPromoteResult>(`/api/admin/library/${p.id}/daily`, { method: 'POST' }),
    onSuccess: (r, p) => {
      const instead = r.replaced ? `, in place of “${r.replaced.title}”` : '';
      setMessage(`“${p.title}” is today’s ${r.kind} daily${instead}.`);
      refresh();
      void client.invalidateQueries({ queryKey: ['home'] });
    },
    onError: (err: Error) => setMessage(err.message),
  });
  const merge = useMutation({
    mutationFn: (body: { keep: string; merge: string }) =>
      api('/api/admin/library/merge', { method: 'POST', json: body }),
    onSuccess: () => {
      setMessage('Merged: the duplicate’s sources, solves, ratings and collections moved over.');
      setPicked([]);
      setKeep(null);
      refresh();
    },
    onError: (err: Error) => setMessage(err.message),
  });
  const rows = data.data?.puzzles ?? [];
  const byId = (id: string) => rows.find((r) => r.id === id);
  const toggle = (id: string) =>
    setPicked((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id].slice(-2)));
  const pair = picked.length === 2 ? picked.map(byId).filter(Boolean) : [];

  return (
    <>
      <h1 className="m-0 text-[26px] font-bold">Library tools</h1>
      <p className="m-0 text-[14px] text-muted">
        Hide a puzzle to take it out of the library (and dailies) without losing anything; delete it
        to remove it with its solves. Make daily puts it in place of today’s daily of its kind
        (classic 9×9 or variant). Tick two duplicates to merge one into the other.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          aria-label="Find puzzles"
          placeholder="Title, setter or id"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-10 min-w-0 flex-1 rounded-btn border border-line bg-field px-3 text-[14px] text-ink"
        />
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={onlyHidden}
            onChange={(e) => setOnlyHidden(e.target.checked)}
          />
          Hidden only
        </label>
      </div>
      {message && (
        <p role="status" className="m-0 text-[14px]">
          {message}
        </p>
      )}
      {pair.length === 2 && (
        <Panel title="Merge duplicates">
          <div role="radiogroup" aria-label="Keep" className="flex flex-col gap-1.5 text-[14px]">
            {pair.map((p) => (
              <label key={p!.id} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="keep"
                  checked={keep === p!.id}
                  onChange={() => setKeep(p!.id)}
                />
                Keep “{p!.title}” ({p!.source}, {when(p!.createdAt)}, {p!.solves} solves)
              </label>
            ))}
          </div>
          <button
            type="button"
            className={`${tinyButton} self-start`}
            disabled={!keep || merge.isPending}
            onClick={() => {
              const other = picked.find((id) => id !== keep)!;
              merge.mutate({ keep: keep!, merge: other });
            }}
          >
            Merge into the kept one
          </button>
        </Panel>
      )}
      <Panel title={`${rows.length}${rows.length === 60 ? '+' : ''} puzzles`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-muted">
                <th className={tableCell} />
                <th className={`${tableCell} font-semibold`}>Puzzle</th>
                <th className={`${tableCell} font-semibold`}>Source</th>
                <th className={`${tableCell} font-semibold`}>Added</th>
                <th className={`${tableCell} text-right font-semibold`}>Solves</th>
                <th className={`${tableCell} font-semibold`}>Score</th>
                <th className={tableCell} />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className={p.hidden ? 'text-muted' : ''}>
                  <td className={tableCell}>
                    <input
                      type="checkbox"
                      aria-label={`Pick ${p.title}`}
                      checked={picked.includes(p.id)}
                      onChange={() => toggle(p.id)}
                    />
                  </td>
                  <td className={`${tableCell} font-semibold`}>
                    <Link to={`/library/${p.id}`}>{p.title}</Link>
                    {p.hidden && <span className="font-normal"> · hidden</span>}
                    {p.inPool && <span className="font-normal"> · in the pool</span>}
                    {p.status === 'needs-plugin' && (
                      <span className="font-normal"> · needs a plugin</span>
                    )}
                  </td>
                  <td className={tableCell}>
                    {p.source}
                    {p.setter ? ` · ${p.setter}` : ''}
                  </td>
                  <td className={tableCell}>{when(p.createdAt)}</td>
                  <td className={`${tableCell} text-right tabular-nums`}>{p.solves}</td>
                  <td className={tableCell}>
                    <ScoreChip score={p.score} />
                  </td>
                  <td className={`${tableCell} whitespace-nowrap text-right`}>
                    {p.status === 'ready' && (
                      <>
                        <button
                          type="button"
                          className={tinyButton}
                          disabled={daily.isPending}
                          onClick={() => {
                            if (window.confirm(`Make “${p.title}” today’s daily?`)) daily.mutate(p);
                          }}
                        >
                          Make daily
                        </button>{' '}
                      </>
                    )}
                    <button type="button" className={tinyButton} onClick={() => hide.mutate(p)}>
                      {p.hidden ? 'Show' : 'Hide'}
                    </button>{' '}
                    <button
                      type="button"
                      className={tinyButton}
                      onClick={() => {
                        const what = p.solves
                          ? ` and its ${p.solves} solve${p.solves === 1 ? '' : 's'}`
                          : '';
                        if (window.confirm(`Delete “${p.title}”${what}? This can’t be undone.`))
                          remove.mutate(p);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
